import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBell, 
  faCheck, 
  faCheckDouble, 
  faUser, 
  faFile, 
  faShoppingCart, 
  faGraduationCap,
  faExclamationTriangle,
  faInfoCircle,
  faFilter,
  faSpinner,
  faEye,
  faChevronLeft,
  faChevronRight,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import notificacaoService from '../../services/notificacaoService';
import Toast from '../../components/Toast';

// Mapeamento de ícones por tipo
const ICONES_TIPO = {
  // Notificações para operadores
  aluno_cadastrado: faUser,
  aluno_atualizado: faUser,
  documento_enviado: faFile,
  documento_atualizado: faFile,
  pedido_criado: faShoppingCart,
  pedido_atualizado: faShoppingCart,
  nota_lancada: faGraduationCap,
  nota_atualizada: faGraduationCap,
  // Notificações para coordenadores
  pedido_status_alterado: faShoppingCart,
  documento_aprovado: faFile,
  documento_rejeitado: faFile
};

// Mapeamento de cores por prioridade
const CORES_PRIORIDADE = {
  urgente: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' },
  alta: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' },
  normal: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
  baixa: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200' }
};

// Nomes legíveis dos tipos
const NOMES_TIPO = {
  // Notificações para operadores
  aluno_cadastrado: 'Aluno Cadastrado',
  aluno_atualizado: 'Aluno Atualizado',
  documento_enviado: 'Documento Enviado',
  documento_atualizado: 'Documento Atualizado',
  pedido_criado: 'Pedido Criado',
  pedido_atualizado: 'Pedido Atualizado',
  nota_lancada: 'Nota Lançada',
  nota_atualizada: 'Nota Atualizada',
  // Notificações para coordenadores
  pedido_status_alterado: 'Status do Pedido',
  documento_aprovado: 'Documento Aprovado',
  documento_rejeitado: 'Documento Rejeitado'
};

// Nomes legíveis dos módulos
const NOMES_MODULO = {
  alunos: 'Alunos',
  documentos: 'Documentos',
  pedidos: 'Pedidos',
  notas: 'Notas'
};

const NotificacoesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // States
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Filtros
  const [filtros, setFiltros] = useState({
    tipo: '',
    modulo: '',
    apenasNaoLidas: false,
    prioridade: '',
    dataInicio: '',
    dataFim: ''
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [selecionadas, setSelecionadas] = useState([]);

  // Resumo
  const [resumo, setResumo] = useState({
    totalNaoLidas: 0,
    urgentes: 0,
    porTipo: {}
  });

  // Verificar se o usuário pode ver notificações (operadores nível >= 4 OU coordenadores nível 2-3)
  const podeVerNotificacoes = user?.nivel_acesso >= 2;

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const carregarNotificacoes = useCallback(async () => {
    if (!podeVerNotificacoes) return;

    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filtros
      };

      // Remover filtros vazios
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === false) {
          delete params[key];
        }
      });

      const response = await notificacaoService.listar(params);
      if (response.success) {
        setNotificacoes(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar notificações:', error);
      showToast('Erro ao carregar notificações', 'error');
    } finally {
      setLoading(false);
    }
  }, [podeVerNotificacoes, pagination.page, pagination.limit, filtros]);

  const carregarResumo = useCallback(async () => {
    if (!podeVerNotificacoes) return;

    try {
      const response = await notificacaoService.obterResumo();
      if (response.success) {
        setResumo({
          totalNaoLidas: response.data.totalNaoLidas || 0,
          urgentes: response.data.urgentes || 0,
          porTipo: response.data.porTipo || {}
        });
      }
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
    }
  }, [podeVerNotificacoes]);

  useEffect(() => {
    carregarNotificacoes();
  }, [carregarNotificacoes]);

  useEffect(() => {
    carregarResumo();
  }, [carregarResumo]);

  const handleMarcarComoLida = async (notificacao) => {
    try {
      setLoadingAction(true);
      await notificacaoService.marcarComoVisualizada(notificacao.id);
      await carregarNotificacoes();
      await carregarResumo();
      showToast('Notificação marcada como lida');
    } catch (error) {
      console.error('Erro ao marcar notificação:', error);
      showToast('Erro ao marcar notificação', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleMarcarSelecionadasComoLidas = async () => {
    if (selecionadas.length === 0) return;

    try {
      setLoadingAction(true);
      await notificacaoService.marcarMultiplasComoVisualizadas(selecionadas);
      setSelecionadas([]);
      await carregarNotificacoes();
      await carregarResumo();
      showToast(`${selecionadas.length} notificação(ões) marcada(s) como lida(s)`);
    } catch (error) {
      console.error('Erro ao marcar notificações:', error);
      showToast('Erro ao marcar notificações', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleMarcarTodasComoLidas = async () => {
    try {
      setLoadingAction(true);
      await notificacaoService.marcarTodasComoVisualizadas();
      await carregarNotificacoes();
      await carregarResumo();
      showToast('Todas as notificações foram marcadas como lidas');
    } catch (error) {
      console.error('Erro ao marcar todas as notificações:', error);
      showToast('Erro ao marcar notificações', 'error');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleNotificacaoClick = async (notificacao) => {
    // Marcar como lida se ainda não foi
    if (!notificacao.visualizada) {
      try {
        await notificacaoService.marcarComoVisualizada(notificacao.id);
        await carregarResumo();
        await carregarNotificacoes();
      } catch (error) {
        console.error('Erro ao marcar notificação:', error);
      }
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleLimparFiltros = () => {
    setFiltros({
      tipo: '',
      modulo: '',
      apenasNaoLidas: false,
      prioridade: '',
      dataInicio: '',
      dataFim: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSelecionar = (id) => {
    setSelecionadas(prev => 
      prev.includes(id) 
        ? prev.filter(s => s !== id)
        : [...prev, id]
    );
  };

  const handleSelecionarTodas = () => {
    const notificacoesNaoLidas = notificacoes.filter(n => !n.visualizada).map(n => n.id);
    if (selecionadas.length === notificacoesNaoLidas.length) {
      setSelecionadas([]);
    } else {
      setSelecionadas(notificacoesNaoLidas);
    }
  };

  const formatarData = (data) => {
    const dataObj = new Date(data);
    return dataObj.toLocaleDateString('pt-BR') + ' às ' + dataObj.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatarDataRelativa = (data) => {
    const dataObj = new Date(data);
    const agora = new Date();
    const diff = agora - dataObj;
    const minutos = Math.floor(diff / 60000);
    const horas = Math.floor(diff / 3600000);
    const dias = Math.floor(diff / 86400000);

    if (minutos < 1) return 'Agora mesmo';
    if (minutos < 60) return `${minutos} min atrás`;
    if (horas < 24) return `${horas}h atrás`;
    if (dias === 1) return 'Ontem';
    if (dias < 7) return `${dias} dias atrás`;
    return dataObj.toLocaleDateString('pt-BR');
  };

  if (!podeVerNotificacoes) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FontAwesomeIcon icon={faBell} className="text-6xl text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-600">Acesso Restrito</h2>
          <p className="text-gray-500 mt-2">
            Você não tem permissão para visualizar notificações do sistema.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <FontAwesomeIcon icon={faBell} className="mr-3 text-blue-600" />
            Notificações
          </h1>
          <p className="text-gray-600 mt-1">
            Gerencie as notificações de solicitações dos coordenadores
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className={`px-4 py-2 rounded-lg border flex items-center space-x-2 transition ${
              mostrarFiltros ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FontAwesomeIcon icon={faFilter} />
            <span>Filtros</span>
          </button>

          {resumo.totalNaoLidas > 0 && (
            <button
              onClick={handleMarcarTodasComoLidas}
              disabled={loadingAction}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center space-x-2 disabled:opacity-50"
            >
              {loadingAction ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faCheckDouble} />
              )}
              <span>Marcar todas como lidas</span>
            </button>
          )}
        </div>
      </div>

      {/* Cards de resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Não Lidas</p>
              <p className="text-2xl font-bold text-gray-800">{resumo.totalNaoLidas}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FontAwesomeIcon icon={faBell} className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Urgentes</p>
              <p className="text-2xl font-bold text-gray-800">{resumo.urgentes}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pedidos</p>
              <p className="text-2xl font-bold text-gray-800">{resumo.porTipo?.pedido_criado || 0}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <FontAwesomeIcon icon={faShoppingCart} className="text-green-600 text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Documentos</p>
              <p className="text-2xl font-bold text-gray-800">{resumo.porTipo?.documento_enviado || 0}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <FontAwesomeIcon icon={faFile} className="text-purple-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Painel de filtros */}
      {mostrarFiltros && (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700">Filtros</h3>
            <button
                onClick={handleLimparFiltros}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Limpar filtros
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                <select
                  value={filtros.tipo}
                  onChange={(e) => handleFiltroChange('tipo', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos</option>
                  {Object.entries(NOMES_TIPO).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Módulo</label>
                <select
                  value={filtros.modulo}
                  onChange={(e) => handleFiltroChange('modulo', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todos</option>
                  {Object.entries(NOMES_MODULO).map(([key, value]) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                <select
                  value={filtros.prioridade}
                  onChange={(e) => handleFiltroChange('prioridade', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todas</option>
                  <option value="urgente">Urgente</option>
                  <option value="alta">Alta</option>
                  <option value="normal">Normal</option>
                  <option value="baixa">Baixa</option>
                </select>
              </div>

              <div className="flex items-end">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filtros.apenasNaoLidas}
                    onChange={(e) => handleFiltroChange('apenasNaoLidas', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Apenas não lidas</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Inicial</label>
                <input
                  type="date"
                  value={filtros.dataInicio}
                  onChange={(e) => handleFiltroChange('dataInicio', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Final</label>
                <input
                  type="date"
                  value={filtros.dataFim}
                  onChange={(e) => handleFiltroChange('dataFim', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Ações em lote */}
        {selecionadas.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
            <span className="text-blue-700">
              {selecionadas.length} notificação(ões) selecionada(s)
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setSelecionadas([])}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={handleMarcarSelecionadasComoLidas}
                disabled={loadingAction}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                Marcar como lidas
              </button>
            </div>
          </div>
        )}

        {/* Lista de notificações */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Cabeçalho da lista */}
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {notificacoes.some(n => !n.visualizada) && (
                <input
                  type="checkbox"
                  checked={selecionadas.length > 0 && selecionadas.length === notificacoes.filter(n => !n.visualizada).length}
                  onChange={handleSelecionarTodas}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              )}
              <span className="text-sm font-medium text-gray-700">
                {pagination.total} notificação(ões)
              </span>
            </div>
          </div>

          {/* Notificações */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FontAwesomeIcon icon={faSpinner} spin className="text-gray-400 text-3xl" />
            </div>
          ) : notificacoes.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faBell} className="text-5xl text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhuma notificação encontrada</p>
            </div>
          ) : (
            <div className="divide-y">
              {notificacoes.map((notificacao) => {
                const corPrioridade = CORES_PRIORIDADE[notificacao.prioridade] || CORES_PRIORIDADE.normal;
                
                return (
                  <div
                    key={notificacao.id}
                    className={`px-4 py-4 hover:bg-gray-50 transition ${
                      !notificacao.visualizada ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Checkbox */}
                      {!notificacao.visualizada && (
                        <input
                          type="checkbox"
                          checked={selecionadas.includes(notificacao.id)}
                          onChange={() => handleSelecionar(notificacao.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      )}

                      {/* Ícone */}
                      <div 
                        className={`p-3 rounded-full ${corPrioridade.bg} ${corPrioridade.text} flex-shrink-0`}
                      >
                        <FontAwesomeIcon 
                          icon={ICONES_TIPO[notificacao.tipo] || faInfoCircle} 
                        />
                      </div>

                      {/* Conteúdo */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`font-medium ${!notificacao.visualizada ? 'text-gray-900' : 'text-gray-700'}`}>
                              {notificacao.titulo}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {notificacao.mensagem}
                            </p>
                          </div>

                          {/* Badge de prioridade */}
                          {notificacao.prioridade !== 'normal' && (
                            <span className={`ml-2 px-2 py-1 text-xs rounded-full ${corPrioridade.bg} ${corPrioridade.text}`}>
                              {notificacao.prioridade}
                            </span>
                          )}
                        </div>

                        {/* Metadados */}
                        <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-400">
                          <span title={formatarData(notificacao.created_at)}>
                            <FontAwesomeIcon icon={faCalendar} className="mr-1" />
                            {formatarDataRelativa(notificacao.created_at)}
                          </span>
                          {notificacao.criador_nome && (
                            <span>
                              <FontAwesomeIcon icon={faUser} className="mr-1" />
                              {notificacao.criador_nome}
                            </span>
                          )}
                          {notificacao.extensao_nome && (
                            <span className="bg-gray-100 px-2 py-0.5 rounded">
                              {notificacao.extensao_nome}
                            </span>
                          )}
                          {notificacao.visualizada && (
                            <span className="text-green-500">
                              <FontAwesomeIcon icon={faEye} className="mr-1" />
                              Lida
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Ações */}
                      {!notificacao.visualizada && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarcarComoLida(notificacao);
                          }}
                          className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-full transition"
                          title="Marcar como lida"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Paginação */}
          {pagination.totalPages > 1 && (
            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page <= 1}
                  className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            </div>
          )}
        </div>

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </div>
  );
};

export default NotificacoesPage;
